// Geraeteinterne Einstellungen lesen und setzen (get_settings / set_settings).
//
// Das sind die Werte UNTERHALB dieser App: Einschaltlautstaerke, Einschalt-
// quelle, Tastensperren, Energiesparen, Hotel-Modus - am Geraet im Service-
// Menue (Samsung) bzw. Pro:Centric-/Installer-Menue (LG). Bisher kam man daran
// nur mit der Service-Fernbedienung vor dem Geraet; eine zu laute
// Einschaltlautstaerke kostete eine Zimmerbegehung.
//
// Diese Klasse ist die Plattform-Weiche und der einzige Ort, an dem die Werte
// in die Form gebracht werden, die auf dem Draht steht:
//
//   Schalter -> true/false   (das Geraet spricht 'on'/'off' bzw. 1/0)
//   Zahlen   -> Number
//   Rest     -> String
//
// Ohne diese Umwandlung meldete ein Samsung 'on' und ein LG 1 fuer denselben
// Schalter, und der Admin muesste beide Schreibweisen kennen.
//
// Antwort auf get_settings:
//   { values: {key: wert}, supported: [key], details: {key: 'via …'} }
// Antwort auf set_settings:
//   { values: {key: wert nach dem Setzen}, failed: {key: Grund} }
//
// `supported` ist der Punkt, an dem sich die Kandidaten-Namen aus dem Katalog
// auf echter Hardware verifizieren lassen: was antwortet, steht drin.

import DeviceType from '@/helpers/DeviceType';
import { DEVICE_SETTINGS, SETTING_TYPE, settingByKey } from './catalog';
import SettingsTizen from './Tizen';
import SettingsLG from './LG';

// Schluesselpraefix fuer die Menuepunkte, die erst am Geraet gefunden werden
// (`menu:brightness`). Sie stehen nicht im Katalog - der Admin baut die Zeilen
// aus dem, was die Sonde gemeldet hat.
export const MENU_PREFIX = 'menu:';

// Samsung meldet Schalter als 'on'/'off', LG als 1/0, manche Firmware als
// 'true'. Alles davon soll auf dem Draht derselbe Boolean sein.
const TRUE_WORDS = ['on', 'true', '1', 'yes', 'enable', 'enabled'];
const FALSE_WORDS = ['off', 'false', '0', 'no', 'disable', 'disabled'];

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const v = String(value).trim().toLowerCase();
  if (TRUE_WORDS.indexOf(v) !== -1) return true;
  if (FALSE_WORDS.indexOf(v) !== -1) return false;
  return undefined;
}

/** Rohwert des Geraets -> Wert fuer den Draht. undefined = unbrauchbar. */
function normalize(entry, raw) {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (entry.type === SETTING_TYPE.SWITCH) return toBoolean(raw);
  if (entry.type === SETTING_TYPE.NUMBER) {
    const n = Number(raw);
    return isNaN(n) ? undefined : n;
  }
  return String(raw);
}

/** Wert vom Draht -> was an den Adapter geht. undefined = nicht setzbar. */
function accept(entry, value) {
  if (entry.type === SETTING_TYPE.SWITCH) return toBoolean(value);
  if (entry.type === SETTING_TYPE.NUMBER) {
    const n = Number(value);
    return isNaN(n) ? undefined : n;
  }
  if (value === null || value === undefined) return undefined;
  return String(value);
}

function adapter() {
  if (DeviceType.isTizen || DeviceType.isOrsay) return SettingsTizen;
  if (DeviceType.isLG) return SettingsLG;
  return null;
}

function platformName() {
  if (DeviceType.isTizen) return 'samsung-tizen';
  if (DeviceType.isOrsay) return 'samsung-orsay';
  if (DeviceType.isLG) return 'lg';
  return 'browser';
}

export default class DeviceSettings {
  /**
   * Was das Geraet an Einstellungs-APIs mitbringt: Namensraum -> Methodennamen.
   *
   * Die Menue-Ids und Property-Namen im Katalog sind zum Teil geraten. Diese
   * Aufzaehlung ist das Gegenstueck: sie sagt, was tatsaechlich da ist. Sie
   * faehrt in der Antwort an den Admin mit und steht auf der Info-Seite -
   * damit laesst sich Modell fuer Modell festhalten, welche Namen tragen,
   * ohne die App anzufassen und ohne jemanden vor das Geraet zu schicken.
   */
  static apiNames() {
    const impl = adapter();
    if (!impl || typeof impl.apiNames !== 'function') return {};
    try {
      return impl.apiNames() || {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Alle Einstellungen lesen. Liefert immer ein Ergebnis, nie einen Fehler.
   *
   * `options.describe` schaltet das Nachfragen bei Menue-Ids OHNE Wert frei -
   * true fuer alle oder eine Id-Liste. Standard ist aus: genau dort lag der
   * Neustart eines U800F (siehe Tizen.js, probeMenuItem).
   */
  static read(options) {
    const impl = adapter();
    const result = {
      values: {},
      supported: [],
      details: {},
      platform: platformName(),
      api: DeviceSettings.apiNames(),
      // Setzbar, aber nicht lesbar: die Menuesprache auf Samsung. Ohne diese
      // Liste haelt der Admin die Zeile fuer "nicht unterstuetzt" und sperrt
      // sie - "lesbar" und "setzbar" sind hier zwei verschiedene Dinge.
      writable: DEVICE_SETTINGS.filter(
        (e) =>
          e.tizen &&
          (e.tizen.writeOnly || e.tizen.call) &&
          (DeviceType.isTizen || DeviceType.isOrsay)
      ).map((e) => e.key),
    };
    if (!impl || !impl.available()) {
      result.error = 'Geraet bietet keine Einstellungs-API (' + result.platform + ')';
      return Promise.resolve(result);
    }

    // Adapter, die zwischen den Eintraegen etwas zwischenspeichern, raeumen
    // vorher auf (auf LG das Hotel-Dokument, das alle Eintraege teilen).
    if (typeof impl.begin === 'function') {
      try {
        impl.begin();
      } catch (e) {
        /* ein Adapter ohne Zwischenspeicher hat nichts aufzuraeumen */
      }
    }

    // Je Einstellung ein Versuch, alle nebenlaeufig: auf LG haengt an jedem
    // Kandidaten ein Timeout, und nacheinander waere das Minuten statt
    // Sekunden.
    const tasks = DEVICE_SETTINGS.map((entry) =>
      Promise.resolve()
        .then(() => impl.read(entry))
        .catch(() => null)
        .then((hit) => {
          if (!hit) return;
          // Der Adapter hat den Weg gefunden, das Geraet aber verweigert die
          // Auskunft (auf einem BU800: "Fehler 9" auf jeden systemcontrol-
          // Getter). Das ist kein fehlender Wert, sondern eine Antwort - und
          // sie gehoert an die Zeile, sonst steht dort nur ein ratloses "nicht
          // unterstuetzt".
          if (hit.value === undefined && hit.error) {
            result.details[entry.key] = hit.via + ' -> ' + hit.error;
            return;
          }
          const value = normalize(entry, hit.value);
          if (value === undefined) {
            // Geantwortet, aber unbrauchbar - das ist ein Fund und keine
            // Luecke: der Rohwert steht in den Details, damit auf echter
            // Hardware sichtbar wird, was die Firmware da liefert.
            result.details[entry.key] = hit.via + ' -> ' + JSON.stringify(hit.value) + ' (nicht verwertbar)';
            return;
          }
          result.values[entry.key] = value;
          result.supported.push(entry.key);
          result.details[entry.key] = hit.via;
        })
    );

    return Promise.all(tasks)
      .then(() => {
        // Was ein Adapter ausser dem Katalog noch herausfindet - auf Samsung
        // die Menuepunkte, die das Geraet ueberhaupt kennt.
        if (typeof impl.extras !== 'function') return null;
        return Promise.resolve()
          .then(() => impl.extras({ describe: options && options.describe }))
          .catch(() => null);
      })
      .then((extras) => {
        if (extras) Object.assign(result, extras);
        return result;
      });
  }

  /**
   * Einzelne Einstellungen setzen. `values` ist {key: wert} vom Admin.
   *
   * Gemeldet wird der Wert, den das Geraet DANACH hat - nicht der, den der
   * Admin geschickt hat. Manche Firmware quittiert ein setItemValue mit true
   * und uebernimmt den Wert trotzdem nicht; nur der Rueckvergleich zeigt das.
   */
  static write(values) {
    const impl = adapter();
    const result = { values: {}, failed: {}, details: {}, platform: platformName() };
    const keys = Object.keys(values || {});
    if (!impl || !impl.available()) {
      const reason = 'Geraet bietet keine Einstellungs-API (' + result.platform + ')';
      keys.forEach((k) => {
        result.failed[k] = reason;
      });
      result.error = reason;
      return Promise.resolve(result);
    }

    if (typeof impl.begin === 'function') {
      try {
        impl.begin();
      } catch (e) {
        /* siehe read() */
      }
    }

    const tasks = keys.map((key) => {
      // `menu:<id>` adressiert einen Menuepunkt, den die Sonde am Geraet
      // gefunden hat - der steht nicht im Katalog, sondern kommt vom Geraet.
      if (key.indexOf(MENU_PREFIX) === 0) {
        const id = key.slice(MENU_PREFIX.length);
        if (typeof impl.writeMenuItem !== 'function') {
          result.failed[key] = 'Menuepunkte sind auf dieser Plattform nicht setzbar';
          return Promise.resolve();
        }
        return Promise.resolve()
          .then(() => impl.writeMenuItem(id, values[key]))
          .catch((e) => ({ ok: false, detail: String((e && (e.message || e.errorMessage)) || e) }))
          .then((res) => {
            const outcome = res || { ok: false, detail: 'keine Antwort' };
            if (outcome.via) result.details[key] = outcome.via;
            if (!outcome.ok) {
              result.failed[key] = outcome.detail || 'abgelehnt';
              return;
            }
            result.values[key] = outcome.value;
            // Der Vergleich laeuft ueber den Zahlenwert: das Geraet meldet 13,
            // der Admin hat 13 geschickt - als Text waeren '13' und 13 zwei
            // verschiedene Dinge.
            if (String(outcome.value) !== String(values[key])) {
              result.failed[key] =
                'Geraet meldet ' + JSON.stringify(outcome.value) + ' statt ' + JSON.stringify(values[key]);
            }
          });
      }

      const entry = settingByKey(key);
      if (!entry) {
        result.failed[key] = 'unbekannte Einstellung';
        return Promise.resolve();
      }
      const wanted = accept(entry, values[key]);
      if (wanted === undefined) {
        result.failed[key] = 'Wert nicht verwertbar: ' + JSON.stringify(values[key]);
        return Promise.resolve();
      }
      return Promise.resolve()
        .then(() => impl.write(entry, wanted))
        .catch((e) => ({ ok: false, detail: String((e && (e.message || e.errorMessage)) || e) }))
        .then((res) => {
          const outcome = res || { ok: false, detail: 'keine Antwort' };
          if (outcome.via) result.details[key] = outcome.via;
          if (!outcome.ok) {
            result.failed[key] = outcome.detail || 'abgelehnt';
            return;
          }
          const after = normalize(entry, outcome.value);
          if (after === undefined) {
            // Gesetzt, aber nicht zurueckzulesen. Kein Fehler - manche Werte
            // greifen erst nach einem Neustart -, aber auch keine Bestaetigung:
            // gemeldet wird der Wunschwert mit Hinweis.
            result.values[key] = wanted;
            result.details[key] = (result.details[key] || '') + ' (nicht zurueckgelesen)';
            return;
          }
          result.values[key] = after;
          if (after !== wanted) {
            result.failed[key] =
              'Geraet meldet ' + JSON.stringify(after) + ' statt ' + JSON.stringify(wanted);
          }
        });
    });

    return Promise.all(tasks).then(() => result);
  }
}



// WEBPACK FOOTER //
// ./src/helpers/DeviceSettings/index.js